import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    crudStatusEnum,
    getCrudStatusLabel,
    getPriceTypeLabel,
    localStorageKeys,
    permissionLevelEnum,
    systemConfigKeyEnum,
} from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateUtilityVehicleMutation } from '../../../../hooks/mutations/useUtilityVehicleMutation';
import { useUtilityVehiclesQuery } from '../../../../hooks/queries/useUtilityVehiclesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { UtilityVehicle } from '../../../../services/apiSLTP/materials/utilityVehicles';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatNumber } from '../../../../utils/numbers';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import { InheritedPriceDisplay } from '../../../domain/InheritedPriceDisplay';
import LicensePlate from '../../../domain/LicensePlate';
import UpdateUtilityVehicleModal from '../../../modals/admin/vehicles/UpdateUtilityVehicleModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface VehiclesTableProps {
    withUsageCount: boolean;
}

const VehiclesTable: React.FC<VehiclesTableProps> = ({ withUsageCount }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedUtilityVehicle, setSelectedUtilityVehicle] = useState<UtilityVehicle | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data, isLoading, refetch } = useUtilityVehiclesQuery({
        queries: { withUsage: withUsageCount },
    });

    const { mutate: mutateUtilityVehicle } = useUpdateUtilityVehicleMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating utilityVehicle:', error.message),
    });

    const sortedData = () =>
        data?.sort((a, b) => {
            const typeA = a.vehicleTypeId?.displayOrder || 0;
            const typeB = b.vehicleTypeId?.displayOrder || 0;
            if (typeA < typeB) return 1;
            if (typeA > typeB) return -1;
            return a.designation.localeCompare(b.designation);
        }) ?? [];

    const columnDefs: ColDef[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
            initialWidth: 150,
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyUtilityVehicle'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedUtilityVehicle(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteUtilityVehicle'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedUtilityVehicle(params.data);
                                    setIsEditMode(false);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('plateNumber')),
            field: 'plateNumber',
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) => {
                if (!params.data) return params.value;
                return <LicensePlate number={params.data.plateNumber} />;
            },
            colId: 'plateNumber',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'vehicleTypeId.designation',
            colId: 'type',
        },
        {
            headerName: capitalizeFirstLetter(t('driver')),
            valueGetter: (params) => formatName(params.data.userId?.firstName, params.data.userId?.lastName),
            colId: 'driver',
        },
        {
            headerName: capitalizeFirstLetter(t('price') + ` (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`),
            filter: 'agNumberColumnFilter',
            valueGetter: (params) => params.data.price ?? params.data.vehicleTypeId?.price,
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.vehicleTypeId?.price}
                        format={(value) => formatNumber(value, t('locale'))}
                    />
                );
            },
            colId: 'price',
            type: 'numericColumn',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeLabel(params.data?.priceType || params.data?.truckTypeId?.priceType))),
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.vehicleTypeId?.priceType}
                        format={(value) => capitalizeFirstLetter(t(getPriceTypeLabel(value)))}
                    />
                );
            },
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('baseName')),
            field: 'baseName',
            colId: 'baseName',
        },
        createDateTimeColumn<UtilityVehicle>('createdAt', 'createdAt'),
        createDateTimeColumn<UtilityVehicle>('updatedAt', 'updatedAt'),
        ...(user?.profileId.permsLevel === permissionLevelEnum.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      initialWidth: 250,
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                      colId: '_id',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xTotal')),
                      field: 'usageCount.total',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      colId: 'xtotal',
                      filter: 'agNumberColumnFilter',
                  },
                  {
                      headerName: capitalizeFirstLetter(t('xGnrUsages')),
                      field: 'usageCount.gnrUtilityVehiclesUsage',
                      cellClass: 'usage-col',
                      headerClass: 'usage-col',
                      colId: 'xgnrusages',
                      filter: 'agNumberColumnFilter',
                  },
              ]
            : []),
    ];

    return (
        <>
            <GenericAgGridTable
                rowData={sortedData().filter((item) => item.crudStatus !== crudStatusEnum.DELETED)}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-utilityVehicles"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedUtilityVehicle(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addVehicle'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.VEHICLES.VEHICLES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateUtilityVehicleModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    utilityVehicle={selectedUtilityVehicle}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}
            {showDeleteModal && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        if (selectedUtilityVehicle) {
                            mutateUtilityVehicle({
                                utilityVehicleId: selectedUtilityVehicle._id,
                                data: { crudStatus: crudStatusEnum.DELETED },
                            });
                        }
                    }}
                    title={t('vehicle')}
                    itemName={selectedUtilityVehicle?.designation}
                />
            )}
        </>
    );
};

export default VehiclesTable;
